import { FormattedMessage } from 'react-intl';
import { Section } from '../Section';
import Button from '../core/Button';
import { DeliveryMethod } from '@/types/offering';
import { CompleteOffering } from '@/types/offering';
import { formatDate } from '@/helpers/formatting';
import { useMemo } from 'react';

interface OfferingGrantedProps {
  method: DeliveryMethod;
  offering: CompleteOffering;
  createdAt: string;
  orderId?: string;
  ticketId?: string;
}

export const OfferingGranted = ({
  method,
  offering,
  createdAt,
  orderId,
  ticketId,
}: OfferingGrantedProps) => {
  const content = useMemo(() => {
    const customContent = offering.deliveryMethods.find(
      (d) => d.type === method,
    )?.grantedDescription;
    if (customContent) {
      return customContent;
    }

    if (method === 'other') {
      return (
        <p>
          <FormattedMessage
            id="ticketOffer.granted.pickUpCard"
            defaultMessage="Gå till kundtjänst för att hämta ut nytt fysiskt resekort"
          />
        </p>
      );
    }
    return (
      <>
        <p>
          <FormattedMessage
            id="ticketOffer.granted.availableSummer"
            defaultMessage="Erbjudandet är nu tillgängligt och kan nyttjas under sommarlovet."
          />
        </p>
        <p>
          {method === 'app' && (
            <FormattedMessage
              id="ticketOffer.granted.useInApp"
              defaultMessage="Det nyttjas i appen på en enhet som har den angivna e-posten kopplad."
            />
          )}
          {method === 'card' && (
            <FormattedMessage
              id="ticketOffer.granted.useOnCard"
              defaultMessage="Det nyttjas genom det valda kortet."
            />
          )}
        </p>
      </>
    );
  }, [method, offering]);

  return (
    <Section className="gap-5 bg-background-50">
      <h1 className="font-bold">
        <FormattedMessage
          id="ticketOffer.granted.title"
          defaultMessage="Beställningen genomförd"
        />
      </h1>

      <div>
        <p>
          <FormattedMessage
            id="ticketOffer.granted.dateLabel"
            defaultMessage="Datum"
          />
          : {formatDate(createdAt)}
        </p>
        {orderId ? (
          <p>
            <FormattedMessage
              id="ticketOffer.granted.orderIdLabel"
              defaultMessage="BeställningsID"
            />
            : {orderId}
          </p>
        ) : null}
        {ticketId ? (
          <p>
            <FormattedMessage
              id="ticketOffer.granted.ticketIdLabel"
              defaultMessage="BiljettID"
            />
            : {ticketId}
          </p>
        ) : null}
      </div>

      {content}
      <p>
        <FormattedMessage
          id="ticketOffer.granted.closePage"
          defaultMessage="Du kan nu stänga ner sidan."
        />
      </p>
      <Button
        onClick={() => window.location.reload()}
        className="mx-auto mt-10"
      >
        <FormattedMessage
          id="ticketOffer.granted.orderAgain"
          defaultMessage="Gör en till beställning"
        />
      </Button>
    </Section>
  );
};
